import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    FormControl,
    InputLabel,
    OutlinedInput,
    Box,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    TextField,
    InputAdornment,
    Chip,
    CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BiSearch } from 'react-icons/bi';

const useStyles = makeStyles((theme) => ({
    formControl: {
        width: '100%',
    },
    inputRoot: {
        padding: '4px 10px',
        fontSize: '13px',
        minHeight: '32px',
        height: '32px',
    },
    paper: {
        maxHeight: '220px',
        overflow: 'auto',
        marginTop: '2px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        zIndex: 1300,
    },
    searchInput: {
        marginBottom: theme.spacing(0.5),
        '& .MuiOutlinedInput-root': {
            height: '32px',
            fontSize: '12px',
        },
        '& .MuiOutlinedInput-input': {
            padding: '6px 10px',
        },
    },
    listItem: {
        padding: '3px 6px',
        minHeight: '32px',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    checkbox: {
        padding: '2px 6px',
    },
    chipContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '3px',
        marginTop: '6px',
        minHeight: '24px',
    },
    chip: {
        height: '24px',
        fontSize: '11px',
    },
    emptyMessage: {
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: '12px',
    },
}));

/**
 * Componente multi-select para estaciones con búsqueda
 * 
 * @param {Object} props - Props del componente
 * @param {Array} props.stations - Lista de estaciones disponibles
 * @param {Array} props.selected - IDs de estaciones seleccionadas
 * @param {Function} props.onChange - Callback al cambiar selección
 * @param {Function} props.onClose - Callback para cerrar el dropdown
 * @param {boolean} props.disabled - Deshabilitar selector
 * @param {boolean} props.loading - Estado de carga
 * @param {string} props.placeholder - Placeholder del input
 * @param {string} props.searchPlaceholder - Placeholder del filtro
 * @returns {JSX.Element} Multi-select de estaciones
 */
export default function MultiSelectStations({
    stations = [],
    selected = [],
    onChange = () => {},
    onClose = () => {},
    disabled = false,
    loading = false,
    placeholder = 'Seleccionar estaciones',
    searchPlaceholder = 'Buscar estaciones...',
}) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Cerrar dropdown cuando se llama onClose
    React.useEffect(() => {
        if (onClose) {
            setOpen(false);
        }
    }, [onClose]);

    // Filtrar estaciones por búsqueda
    const filteredStations = useMemo(() => {
        if (!searchTerm) return stations;
        const searchLower = searchTerm.toLowerCase();
        return stations.filter((station) =>
            (station.name || station.nombre || '').toLowerCase().includes(searchLower) ||
            (station.id || '').toString().includes(searchLower)
        );
    }, [stations, searchTerm]);

    // Obtener nombres de estaciones seleccionadas
    const selectedStationNames = useMemo(() => {
        return selected
            .map((id) => {
                const station = stations.find((s) => s.id === id);
                return station ? station.name || station.nombre : null;
            })
            .filter(Boolean);
    }, [selected, stations]);

    const handleToggle = (stationId) => {
        const newSelected = selected.includes(stationId)
            ? selected.filter((id) => id !== stationId)
            : [...selected, stationId];
        onChange(newSelected);
    };

    const handleRemoveChip = (stationId) => {
        const newSelected = selected.filter((id) => id !== stationId);
        onChange(newSelected);
    };

    const handleClearAll = () => {
        onChange([]);
        setSearchTerm('');
    };

    return (
        <Box>
            <FormControl variant="outlined" className={classes.formControl} disabled={disabled}>
                
                <OutlinedInput
                   
                    label="Estaciones"
                    className={classes.inputRoot}
                    onClick={() => !disabled && setOpen(!open)}
                    readOnly
                    placeholder={selected.length === 0 ? placeholder : ''}
                    value=""
                    endAdornment={
                        loading ? (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ) : null
                    }
                />
            </FormControl>

            {/* Chips de estaciones seleccionadas */}
            {selected.length > 0 && (
                <Box className={classes.chipContainer}>
                    {selectedStationNames.map((name, index) => (
                        <Chip
                            key={selected[index]}
                            label={name}
                            onDelete={() => handleRemoveChip(selected[index])}
                            size="small"
                            className={classes.chip}
                            color="primary"
                            variant="outlined"
                        />
                    ))}
                </Box>
            )}

            {/* Dropdown con opciones */}
            {open && !disabled && (
                <Paper className={classes.paper}>
                    <Box p={1}>
                        {/* Búsqueda */}
                        <TextField
                            fullWidth
                            className={classes.searchInput}
                            placeholder={searchPlaceholder}
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <BiSearch size={16} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Lista de estaciones */}
                        {filteredStations.length > 0 ? (
                            <List dense>
                                {filteredStations.map((station) => (
                                    <ListItem
                                        key={station.id}
                                        button
                                        className={classes.listItem}
                                        onClick={() => handleToggle(station.id)}
                                    >
                                        <ListItemIcon className={classes.checkbox}>
                                            <Checkbox
                                                edge="start"
                                                checked={selected.includes(station.id)}
                                                tabIndex={-1}
                                                disableRipple
                                                size="small"
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={station.name || station.nombre}
                                            primaryTypographyProps={{ style: { fontSize: '13px' } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Box className={classes.emptyMessage}>
                                No se encontraron estaciones
                            </Box>
                        )}

                        {/* Botones de acción */}
                        {selected.length > 0 && (
                            <Box
                                style={{
                                    padding: '8px',
                                    borderTop: '1px solid #e0e0e0',
                                    textAlign: 'right',
                                }}
                            >
                                <Chip
                                    label="Limpiar selección"
                                    size="small"
                                    onClick={handleClearAll}
                                    variant="outlined"
                                    style={{ cursor: 'pointer' }}
                                />
                            </Box>
                        )}
                    </Box>
                </Paper>
            )}
        </Box>
    );
}

MultiSelectStations.propTypes = {
    stations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string,
            nombre: PropTypes.string,
        })
    ),
    selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    placeholder: PropTypes.string,
    searchPlaceholder: PropTypes.string,
};
